<template>
  <el-dialog
    :visible="visible"
    :show-close="false"
    :close-on-click-modal="false"
    :append-to-body="true"
    width="680px"
    custom-class="room-picker-dialog"
    @close="$emit('update:visible', false)">
    <div class="rp-modal">
      <!-- 头部 -->
      <div class="rp-header">
        <div>
          <div class="rp-title">选择负责房间</div>
          <div class="rp-subtitle">点击单选，拖拽框选，或使用快捷按钮批量选择</div>
        </div>
        <div class="rp-close" @click="$emit('update:visible', false)">
          <i class="el-icon-close"></i>
        </div>
      </div>

      <!-- 搜索 + 快捷按钮 -->
      <div class="rp-toolbar">
        <el-input
          v-model="searchKey"
          placeholder="搜索房间号"
          clearable
          size="small"
          style="width:180px"
          prefix-icon="el-icon-search">
        </el-input>
        <span class="rp-quick-label">快捷：</span>
        <el-button size="mini" @click="selectAll">全选</el-button>
        <el-button size="mini" @click="clearAll">清空</el-button>
        <el-button
          v-for="floor in floors"
          :key="floor"
          size="mini"
          @click="selectFloor(floor)">
          {{ floor }}楼全选
        </el-button>
      </div>

      <!-- 房间列表（按楼层分组） -->
      <div class="rp-body">
        <div v-for="floor in floors" :key="floor" class="rp-floor-group">
          <div class="rp-floor-header">
            <span class="rp-floor-tag">{{ floor }}楼</span>
            <span class="rp-floor-count">已选 {{ selectedByFloor(floor) }} / {{ roomsByFloor(floor).length }} 间</span>
          </div>
          <div
            class="rp-room-grid"
            @mousedown="onGridMouseDown($event)"
            @mousemove="onGridMouseMove"
            @mouseup="onGridMouseUp">
            <div
              v-for="room in filteredRoomsByFloor(floor)"
              :key="room.roomId"
              class="rp-room-card"
              :class="{
                'selected': isSelected(room.roomId),
                'occupied': isOccupied(room),
                'drag-add': isDragging && dragMode === 'select' && isDragSelected(room.roomId),
                'drag-remove': isDragging && dragMode === 'deselect' && isDragSelected(room.roomId)
              }"
              :data-room-id="room.roomId"
              @click="toggleRoom(room)">
              <div class="rp-room-no">{{ room.roomNo }}</div>
              <div class="rp-room-sub">
                {{ isOccupied(room) ? '已有人' : (room.bedNum + '床') }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="!filteredRooms.length" class="rp-empty">暂无房间数据</div>
      </div>

      <!-- 底部 -->
      <div class="rp-footer">
        <div class="rp-selected-preview">
          <i class="el-icon-house" style="color:#2563EB;font-size:14px;flex-shrink:0"></i>
          <span style="color:#64748B;font-size:13px">已选 {{ selectedIds.length }} 间：</span>
          <span v-for="id in selectedIds.slice(0, 6)" :key="id" class="rp-tag">
            {{ getRoomNo(id) }}
          </span>
          <span v-if="selectedIds.length > 6" style="color:#94A3B8;font-size:12px">
            +{{ selectedIds.length - 6 }}间
          </span>
        </div>
        <div>
          <el-button @click="$emit('update:visible', false)">取消</el-button>
          <el-button type="primary" @click="confirm">
            <i class="el-icon-check"></i> 确认选择
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'RoomPicker',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    rooms: {
      type: Array,
      default: () => []
    },
    selected: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchKey: '',
      selectedIds: [],
      // 拖拽框选
      isDragging: false,
      hasDragged: false,
      dragMode: 'select', // 'select' | 'deselect'
      dragHoveredIds: new Set()
    }
  },
  computed: {
    floors() {
      const fs = [...new Set(this.rooms.map(r => r.floorNo))].sort((a, b) => a - b)
      return fs
    },
    filteredRooms() {
      return this.rooms.filter(r => !this.searchKey || r.roomNo.includes(this.searchKey))
    }
  },
  watch: {
    selected(v) {
      this.selectedIds = [...v]
    },
    visible(v) {
      if (v) {
        this.selectedIds = [...this.selected]
        this.searchKey = ''
      }
    }
  },
  methods: {
    isOccupied(room) {
      const checkIn = room.checkInNum || 0
      const bedNum = room.bedNum || 1
      return checkIn > 0 && checkIn >= bedNum
    },
    roomsByFloor(floor) {
      return this.rooms.filter(r => r.floorNo === floor)
    },
    filteredRoomsByFloor(floor) {
      return this.roomsByFloor(floor).filter(r =>
        !this.searchKey || r.roomNo.includes(this.searchKey)
      )
    },
    isSelected(id) {
      return this.selectedIds.includes(id)
    },
    selectedByFloor(floor) {
      return this.roomsByFloor(floor).filter(r => this.isSelected(r.roomId)).length
    },
    getRoomNo(id) {
      const r = this.rooms.find(r => r.roomId === id)
      return r ? r.roomNo : id
    },
    toggleRoom(room) {
      if (this.isOccupied(room)) return
      const idx = this.selectedIds.indexOf(room.roomId)
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1)
      } else {
        this.selectedIds.push(room.roomId)
      }
    },
    selectAll() {
      this.selectedIds = this.rooms.filter(r => !this.isOccupied(r)).map(r => r.roomId)
    },
    clearAll() {
      this.selectedIds = []
    },
    selectFloor(floor) {
      const ids = this.roomsByFloor(floor).filter(r => !this.isOccupied(r)).map(r => r.roomId)
      const existing = this.selectedIds.filter(id => !ids.includes(id))
      this.selectedIds = [...existing, ...ids]
    },
    isDragSelected(id) {
      return this.isDragging && this.dragHoveredIds.has(id)
    },
    onGridMouseDown(e) {
      const card = e.target.closest('.rp-room-card')
      if (!card) return
      const id = Number(card.dataset.roomId)
      const room = this.rooms.find(r => r.roomId === id)
      if (room && this.isOccupied(room)) return
      this.isDragging = true
      this.hasDragged = false
      this.dragMode = this.isSelected(id) ? 'deselect' : 'select'
      this.dragHoveredIds = new Set([id])
    },
    onGridMouseMove(e) {
      if (!this.isDragging) return
      const card = e.target.closest('.rp-room-card')
      if (card) {
        const id = Number(card.dataset.roomId)
        if (!this.dragHoveredIds.has(id)) {
          this.hasDragged = true
          this.dragHoveredIds.add(id)
          // 触发响应式更新
          this.dragHoveredIds = new Set(this.dragHoveredIds)
        }
      }
    },
    onGridMouseUp() {
      if (!this.isDragging) return
      if (this.hasDragged) {
        this.dragHoveredIds.forEach(id => {
          const room = this.rooms.find(r => r.roomId === id)
          if (!room || this.isOccupied(room)) return
          if (this.dragMode === 'select' && !this.selectedIds.includes(id)) {
            this.selectedIds.push(id)
          } else if (this.dragMode === 'deselect') {
            const idx = this.selectedIds.indexOf(id)
            if (idx >= 0) this.selectedIds.splice(idx, 1)
          }
        })
      }
      this.isDragging = false
      this.hasDragged = false
      this.dragHoveredIds = new Set()
    },
    confirm() {
      const rooms = this.selectedIds.map(id => {
        const r = this.rooms.find(x => x.roomId === id)
        return { roomId: id, roomNo: r ? r.roomNo : String(id) }
      })
      this.$emit('confirm', rooms)
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.rp-modal {
  display: flex;
  flex-direction: column;
  max-height: 75vh;
}
.rp-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #F1F5F9;
}
.rp-title { font-size: 16px; font-weight: 600; color: #1E293B; }
.rp-subtitle { font-size: 12px; color: #94A3B8; margin-top: 3px; }
.rp-close {
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; color: #94A3B8;
}
.rp-close:hover { background: #F1F5F9; }
.rp-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 20px;
  border-bottom: 1px solid #F8FAFC;
  background: #FAFAFA;
}
.rp-toolbar .el-input { margin-right: 8px; }
.rp-toolbar .el-button { margin-left: 6px; }
.rp-quick-label { font-size: 12px; color: #94A3B8; margin-left: 8px; }
.rp-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
}
.rp-floor-group { margin-bottom: 16px; }
.rp-floor-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.rp-floor-tag {
  padding: 2px 10px;
  border-radius: 4px;
  background: #EFF6FF;
  color: #2563EB;
  font-size: 12px;
  font-weight: 600;
  margin-right: 8px;
}
.rp-floor-count { font-size: 12px; color: #94A3B8; }
.rp-room-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 8px;
  user-select: none;
}
.rp-room-card {
  padding: 10px 6px;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  border: 1.5px solid #E2E8F0;
  background: #fff;
  transition: all 0.12s;
}
.rp-room-card:hover:not(.occupied) { border-color: #93C5FD; background: #F0F9FF; }
.rp-room-card.selected { border-color: #2563EB; background: #EFF6FF; }
.rp-room-card.occupied { opacity: 0.45; cursor: not-allowed; background: #F8FAFC; }
.rp-room-card.drag-add { border-color: #60A5FA; background: #DBEAFE; }
.rp-room-card.drag-remove { border-color: #FCA5A5; background: #FEE2E2; }
.rp-room-no { font-size: 14px; font-weight: 700; color: #1E293B; }
.rp-room-card.selected .rp-room-no { color: #2563EB; }
.rp-room-sub { font-size: 11px; color: #94A3B8; margin-top: 2px; }
.rp-empty { text-align: center; color: #94A3B8; padding: 30px 0; }
.rp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #E2E8F0;
}
.rp-selected-preview {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
}
.rp-selected-preview > * { margin-right: 6px; }
.rp-tag {
  padding: 2px 8px;
  border-radius: 4px;
  background: #EFF6FF;
  color: #2563EB;
  font-size: 12px;
}
</style>

<style>
/* 全局样式：去掉 el-dialog 默认 padding，让内容自己控制间距 */
.room-picker-dialog .el-dialog__header { display: none; }
.room-picker-dialog .el-dialog__body { padding: 0; }
</style>
